import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import {
  Calendar as CalendarIcon, History, LogOut, LayoutGrid, ListTodo,
  Settings, Crown, HelpCircle, Shield, Headphones, Users, ArrowRight,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const navLink = (path, label, Icon) => (
    <button
      onClick={() => navigate(path)}
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all',
        isActive(path)
          ? 'bg-white text-[#1A1A1A] shadow-[0_1px_2px_rgba(26,26,26,0.06)] border border-[#E5E2DA]'
          : 'text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-white/60'
      )}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );

  return (
    <div className="sticky top-0 z-50 bg-[#FAF9F5]/85 backdrop-blur-md border-b border-[#EEEAE0]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        {/* Brand */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 group flex-shrink-0"
        >
          <div className="w-8 h-8 rounded-md overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png"
              alt="Monk Mode"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="font-serif text-lg tracking-tight">Monk Mode</span>
        </button>

        {/* Center nav */}
        <nav className="flex items-center gap-1 bg-[#F4F3EE]/60 border border-[#EEEAE0] rounded-full px-1 py-1 overflow-x-auto no-scrollbar">
          {navLink('/dashboard', 'Arena', LayoutGrid)}
          {navLink('/weekly', 'Weekly', ListTodo)}
          {navLink('/calendar', 'Calendar', CalendarIcon)}
          {navLink('/history', 'History', History)}
          {navLink('/shared-goals', 'Shared', Users)}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!user?.is_premium ? (
            <button
              onClick={() => navigate('/upgrade')}
              className="sl-pill-coral text-xs px-4 py-2"
              data-testid="upgrade-btn"
            >
              Upgrade <ArrowRight className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/subscription')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#FDF0EC] text-[#B04A32] border border-[#F5D5CB]"
              data-testid="pro-badge"
            >
              <Crown className="w-3 h-3" /> Pro
            </button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-white border border-transparent hover:border-[#E5E2DA] transition"
                data-testid="settings-trigger"
              >
                <Settings className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white border border-[#E5E2DA] rounded-2xl shadow-[0_8px_24px_rgba(26,26,26,0.08)] p-1"
            >
              <div className="px-3 py-2 text-xs text-[#9E8778] font-mono truncate">
                {user?.email}
              </div>
              <DropdownMenuSeparator className="bg-[#EEEAE0]" />
              <DropdownMenuItem
                onClick={() => navigate('/subscription')}
                className="rounded-xl text-sm text-[#1A1A1A] focus:bg-[#F4F3EE]"
              >
                <Settings className="w-4 h-4 mr-2" /> Profile & Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/faq')}
                className="rounded-xl text-sm text-[#1A1A1A] focus:bg-[#F4F3EE]"
              >
                <HelpCircle className="w-4 h-4 mr-2" /> FAQ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/help')}
                className="rounded-xl text-sm text-[#1A1A1A] focus:bg-[#F4F3EE]"
              >
                <Headphones className="w-4 h-4 mr-2" /> Help & Support
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/privacy')}
                className="rounded-xl text-sm text-[#1A1A1A] focus:bg-[#F4F3EE]"
              >
                <Shield className="w-4 h-4 mr-2" /> Privacy
              </DropdownMenuItem>
              {user?.is_admin && (
                <>
                  <DropdownMenuSeparator className="bg-[#EEEAE0]" />
                  <DropdownMenuItem
                    onClick={() => navigate('/admin')}
                    className="rounded-xl text-sm text-[#1A1A1A] focus:bg-[#F4F3EE]"
                  >
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-[#EEEAE0]" />
              <DropdownMenuItem
                onClick={logout}
                className="rounded-xl text-sm text-[#B04A32] focus:bg-[#FDF0EC]"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
